/**
 * Prospects: what Digby could still get to - the exit, and enough diamonds for the quota. A move
 * that is survivable but walls him off (a boulder rolling into the only doorway of a room, a
 * landslide over the way out) is a slow death, so look-ahead compares prospects before and after.
 * The exit is measured in boulders between Digby and it, so being sealed into a room counts even
 * where the way out already needed a boulder moving. The flood first lets everything falling or
 * unsupported drop down its column, so a boulder still on its way into a doorway already counts.
 */

import { E } from '../sim';
import type { CaveState } from '../sim';

const { SPACE, DIRT, STEEL, DIAMOND, DIAMOND_FALLING, BOULDER, BOULDER_FALLING, CLOCK, KEY } = E;
const { OUTBOX_OPEN, OUTBOX_CLOSED, FIREFLY, BUTTERFLY, EXPLODE, DIAMOND_BIRTH, DOOR_OPENING } = E;

/** No way to the exit at all, even through boulders. */
export const NO_EXIT = 0x3fff;

/** Cells the flood crosses freely: diggable, collectable, or about to clear. */
function passable(el: number): boolean {
  if (el === SPACE || el === DIRT || el === DIAMOND || el === CLOCK || el === OUTBOX_OPEN) return true;
  if (el >= KEY && el < KEY + 3) return true;
  if (el >= FIREFLY && el < BUTTERFLY + 4) return true;
  return (el >= EXPLODE && el < DIAMOND_BIRTH + 5) || el === DOOR_OPENING;
}

function isDiamond(el: number): boolean {
  return el === DIAMOND || (el >= DIAMOND_BIRTH && el < DIAMOND_BIRTH + 5);
}

export interface Prospects {
  /** Boulders in the way to the exit (`NO_EXIT` if walls cut it off). */
  exit: number;
  /** Diamonds held plus diamonds in the region Digby can reach without moving boulders. */
  diamonds: number;
  /** Diamonds held plus every diamond he could reach if boulders were moved. */
  reachable: number;
}

export class Flood {
  private readonly seen: Int32Array;
  private readonly cost: Int32Array;
  /** A deque for the 0-1 search: free steps go on the front, boulder crossings on the back. */
  private readonly deque: Int32Array;
  /** The settled picture: a copy of the cells with loose objects dropped. */
  private readonly cells: Uint8Array;
  private mark = 0;

  constructor(private readonly n: number) {
    this.seen = new Int32Array(n);
    this.cost = new Int32Array(n);
    this.deque = new Int32Array(2 * n + 2);
    this.cells = new Uint8Array(n);
  }

  /** Drop every falling or unsupported boulder and diamond straight down as far as it will go. */
  private settle(s: CaveState): Uint8Array {
    const { w, h } = s;
    const c = this.cells;
    c.set(s.cells);
    for (let y = h - 2; y >= 0; y--) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const el = c[i] ?? STEEL;
        const boulder = el === BOULDER || el === BOULDER_FALLING;
        if (!boulder && el !== DIAMOND && el !== DIAMOND_FALLING) continue;
        let j = i;
        while (j + w < c.length && c[j + w] === SPACE) j += w;
        c[i] = SPACE;
        c[j] = boulder ? BOULDER : DIAMOND;
      }
    }
    return c;
  }

  measure(s: CaveState, out: Prospects): Prospects {
    const { w } = s;
    const n = this.n;
    const cells = this.settle(s);
    const { seen, cost, deque } = this;
    const mark = ++this.mark;
    const start = s.digby.y * w + s.digby.x;
    let head = n;
    let tail = n;
    let exit = NO_EXIT;
    let diamonds = s.diamonds;
    let reachable = s.diamonds;
    deque[tail++] = start;
    seen[start] = mark;
    cost[start] = 0;
    while (head < tail) {
      const c = deque[head++] ?? 0;
      const d = cost[c] ?? 0;
      const x = c % w;
      for (let k = 0; k < 4; k++) {
        if ((k === 1 && x === w - 1) || (k === 3 && x === 0)) continue;
        const nb = k === 0 ? c - w : k === 1 ? c + 1 : k === 2 ? c + w : c - 1;
        if (nb < 0 || nb >= n || seen[nb] === mark) continue;
        const el = cells[nb] ?? STEEL;
        if (el === OUTBOX_OPEN || el === OUTBOX_CLOSED) exit = Math.min(exit, d);
        if (passable(el)) {
          seen[nb] = mark;
          cost[nb] = d;
          deque[--head] = nb;
          if (isDiamond(el)) {
            reachable++;
            if (d === 0) diamonds++;
          }
        } else if (el === BOULDER) {
          seen[nb] = mark;
          cost[nb] = d + 1;
          deque[tail++] = nb;
        }
      }
    }
    out.exit = exit;
    out.diamonds = diamonds;
    out.reachable = reachable;
    return out;
  }
}

/** Did `after` lose something `before` had: ground toward the exit, or enough diamonds for the quota? */
export function worse(before: Prospects, after: Prospects, needed: number): boolean {
  if (after.exit > before.exit) return true;
  return after.diamonds < Math.min(before.diamonds, needed);
}
